import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  TaxCode,
  CreateTaxCodeData,
  UpdateTaxCodeData,
  TaxCodesListParams,
  TaxCodeResponse,
  TaxCodesListResponse,
} from '../types/taxCode';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getTaxCodes = async (params?: TaxCodesListParams): Promise<TaxCode[]> => {
  const queryParams = new URLSearchParams();

  if (params?.active != null) {
    queryParams.set('active', params.active ? 'true' : 'false');
  }
  if (params?.is_taxable != null) {
    queryParams.set('is_taxable', params.is_taxable ? 'true' : 'false');
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TAX_CODES.GET_ALL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch tax codes');
  }

  const result: TaxCodesListResponse = await response.json();
  return result.data ?? [];
};

export const getTaxCodeById = async (id: number): Promise<TaxCode> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TAX_CODES.GET_BY_ID}/${id}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch tax code');
  }

  const result: TaxCodeResponse = await response.json();
  return result.data;
};

export const createTaxCode = async (data: CreateTaxCodeData): Promise<TaxCode> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TAX_CODES.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create tax code');
  }

  const result: TaxCodeResponse = await response.json();
  return result.data;
};

export const updateTaxCode = async (id: number, data: UpdateTaxCodeData): Promise<TaxCode> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TAX_CODES.UPDATE}/${id}`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update tax code');
  }

  const result: TaxCodeResponse = await response.json();
  return result.data;
};

export const deleteTaxCode = async (id: number): Promise<void> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TAX_CODES.DELETE}/${id}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete tax code');
  }
};
